<script setup lang="ts">
import {
  computed,
  type CSSProperties,
  inject,
  nextTick,
  onBeforeUnmount,
  reactive,
  ref,
  useSlots,
  useTemplateRef,
  watch,
} from "vue";
import type {
  ControlSize,
  ExtendedDesignVersion,
  Placement,
  PositioningStrategy,
  Point,
} from "../models/types";
import {
  calculateArrowPosition,
  determineTooltipPositionAndPlacement,
  determineTooltipPositionFromMousePosition,
} from "../models/tooltip-positioning";
import {
  CONTROLS_DESIGN_VERSION,
  DEFAULT_CONTROLS_DESIGN_VERSION,
  DEFAULT_DESIGN_VERSION,
  DESIGN_VERSION,
} from "../models";

export type BcTooltipProps = {
  /** Adds arrow to the tooltip */
  arrow?: boolean;
  /** Specifies the tooltip content text */
  content?: string;
  /** Specifies the tooltip content's style */
  contentDesign?: ExtendedDesignVersion;
  /** Disables the tooltip */
  disabled?: boolean;
  /** Specifies the distance of the popup from its reference element. */
  offset?: number[];
  /** Specifies the placement of the tooltip compared to the wrapper element */
  placement?: Placement;
  /**
   * Specifies the popup positioning strategy.
   * @type {PositioningStrategy}
   * @default ['fixed']
   */
  positioningStrategy?: PositioningStrategy;
  /** Makes tooltip content's border rounded */
  rounded?: boolean;
  /** Specifies pre-defined sizes for the tooltip content */
  size?: ControlSize;
  /** Shows tooltip at mouse position */
  mouseMode?: boolean;
  /** Time before tooltip is shown in mouse mode */
  mouseModeDebounceTime?: number;
  /** Sets the target for the tooltip teleport element (default: body) */
  teleportTarget?: string;
};

const slots = useSlots();

const {
  arrow = true,
  content,
  contentDesign = "dark",
  disabled = false,
  offset,
  placement = "top",
  positioningStrategy = "fixed",
  rounded = true,
  size,
  mouseMode = false,
  mouseModeDebounceTime = 500,
  teleportTarget = "body",
} = defineProps<BcTooltipProps>();

const tooltipControlRef = useTemplateRef("tooltipControlRef");
const triggerRef = useTemplateRef("triggerRef");
const tooltipRef = useTemplateRef("tooltipRef");
const arrowRef = useTemplateRef("arrowRef");

// observer for detecting changes in the content slot
const mutationObserver = new MutationObserver(() => onContentChanged());

const tooltipStyles = reactive<CSSProperties>({
  position: positioningStrategy,
  top: 0,
  left: 0,
});

const arrowStyles = reactive<CSSProperties>({
  position: "absolute",
  top: 0,
  left: 0,
});

const isVisible = ref<boolean>(false);
let mouseTimer: number | undefined;
let hasEntered: boolean = false;

const offsetValue = ref(offset);
const arrowValue = ref(arrow);
const roundedValue = ref(rounded);

const maxWidth = computed<number>(() => {
  switch (size) {
    case "sm":
      return 180;
    case "md":
      return 240;
    case "lg":
      return 300;
    default:
      return 0;
  }
});

const design = inject(
  DESIGN_VERSION,
  computed(() => DEFAULT_DESIGN_VERSION),
);
const controlsDesign = inject(
  CONTROLS_DESIGN_VERSION,
  computed(() => DEFAULT_CONTROLS_DESIGN_VERSION),
);
const pak = computed(() => {
  const value = contentDesign === "mouseMode" || mouseMode ? "7" : controlsDesign.value;
  return "bc-controls-d" + (value == "6" ? "6" : "7");
});

if (contentDesign === "mouseMode") {
  offsetValue.value = [8, -8];
  arrowValue.value = false;
  roundedValue.value = true;
}

function showTooltip(): void {
  if (disabled || (!content && !slots.content)) {
    return;
  }
  isVisible.value = true;

  window.addEventListener("scroll", onScroll);
  window.addEventListener("resize", onResize);

  // the tooltip should already be visible when updateTooltip is called,
  // otherwise it has no width and height
  nextTick(() => {
    if (!mouseMode) {
      updateTooltip();
    }
    if (tooltipRef.value) {
      mutationObserver.observe(tooltipRef.value, { childList: true, subtree: true });
    }
  });
}

function hideTooltip(): void {
  isVisible.value = false;

  window.removeEventListener("scroll", onScroll);
  window.removeEventListener("resize", onResize);

  mutationObserver.disconnect();

  updateTooltip();
}

function updateTooltip(): void {
  if (!triggerRef.value || !tooltipRef.value) return;

  if (!isVisible.value) {
    tooltipStyles.transform = undefined;
    tooltipStyles.maxWidth = undefined;
    tooltipStyles.maxHeight = undefined;
    tooltipStyles.overflow = undefined;
    return;
  }

  const arrowElement = (arrow && arrowRef.value) || null;

  // Apply styles to the tooltip
  tooltipStyles.font = tooltipControlRef.value
    ? getComputedStyle(tooltipControlRef.value, null).getPropertyValue("font")
    : undefined;

  nextTick(() => {
    if (!triggerRef.value || !tooltipRef.value) {
      return;
    }

    // Calculate the initial position of the tooltip
    let { position: tooltipPosition, placement: tooltipPlacement } =
      determineTooltipPositionAndPlacement(
        triggerRef.value,
        tooltipRef.value,
        arrowElement,
        placement,
        offsetValue.value,
      );

    // Apply the calculated position to the tooltip and set the maximum size
    const calcWidth = window.innerWidth - tooltipPosition.x - 13;
    tooltipStyles.transform = `translate(${tooltipPosition.x}px, ${tooltipPosition.y}px)`;
    tooltipStyles.maxWidth =
      size && calcWidth < maxWidth.value ? `${calcWidth}px` : undefined;
    tooltipStyles.maxHeight = `${window.innerHeight - tooltipPosition.y - 13}px`;
    tooltipStyles.overflow = "hidden";

    // If not has arrow return
    if (!arrowElement) return;

    // Calculate the arrow's position relative to the tooltip
    const arrowPosition = calculateArrowPosition(
      triggerRef.value,
      tooltipRef.value,
      arrowElement,
      tooltipPlacement,
      tooltipPosition,
    );
    if (arrowPosition) {
      // Apply styles and the calculated position to the arrow
      arrowStyles.transform = `translate(${arrowPosition.x}px, ${arrowPosition.y}px)`;
    }
  });
}

function updateTooltipByMousePosition(mousePosition: Point): void {
  if (!tooltipRef.value) return;

  // Calculate position
  const position = determineTooltipPositionFromMousePosition(
    tooltipRef.value,
    mousePosition,
    offsetValue.value,
  );

  // Apply styles
  const calcWidth = window.innerWidth - position.x - 13;
  tooltipStyles.transform = `translate(${position.x}px, ${position.y}px)`;
  tooltipStyles.maxWidth =
    size && calcWidth < maxWidth.value ? `${calcWidth}px` : undefined;
  tooltipStyles.maxHeight = `${window.innerHeight - position.y - 13}px`;
  tooltipStyles.overflow = "hidden";
  tooltipStyles.font = tooltipControlRef.value
    ? getComputedStyle(tooltipControlRef.value, null).getPropertyValue("font")
    : undefined;
  arrowStyles.transform = undefined;
}

function onClick(): void {
  if (!mouseMode) return;
  onLeave();
}

function onEnter(): void {
  if (mouseMode) {
    hasEntered = true;
    return;
  }
  showTooltip();
}

function onLeave(): void {
  if (mouseTimer) window.clearTimeout(mouseTimer);
  hasEntered = false;
  hideTooltip();
}

function onMove(event: MouseEvent): void {
  if (!mouseMode || !hasEntered) return;

  // reset mouse timer
  if (mouseTimer) window.clearTimeout(mouseTimer);

  // start timer to show if not visible
  if (!isVisible.value) {
    mouseTimer = window.setTimeout(() => {
      showTooltip();
      nextTick(() =>
        updateTooltipByMousePosition({ x: event.clientX, y: event.clientY }),
      );
    }, mouseModeDebounceTime);
  }
}

function onContentChanged(): void {
  if (!isVisible.value) return;
  updateTooltip();
}

function onDisabledChanged(disabled: boolean): void {
  if (!isVisible.value || !disabled) return;
  hideTooltip();
}

function onScroll(): void {
  hideTooltip();
}

function onResize(): void {
  hideTooltip();
}

watch(() => content, onContentChanged); // only observes the property not the slot (slot see below)
watch(() => disabled, onDisabledChanged);

onBeforeUnmount(() => {
  hideTooltip();
});

defineExpose({
  tooltipRef,
  triggerRef,
  arrowRef,
  tooltipStyles,
  arrowStyles,
  isVisible,
  offset,
  arrow,
  rounded,
  onClick,
  onEnter,
  onLeave,
  onMove,
});
</script>

<template>
  <div
    v-bind:data-pak="pak"
    class="bissantzControls tooltipControl"
    ref="tooltipControlRef"
  >
    <div
      ref="triggerRef"
      class="trigger"
      v-bind:data-design="design"
      v-bind:class="{ disabled }"
      v-on:click="onClick"
      v-on:contextmenu="onClick"
      v-on:mouseenter="onEnter"
      v-on:mousemove="onMove"
      v-on:mouseleave="onLeave"
    >
      <slot>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          xmlns:xlink="http://www.w3.org/1999/xlink"
          width="16.25px"
          height="16.25px"
          version="1.2"
          baseProfile="tiny"
          id="Ebene_1"
          x="0px"
          y="0px"
          viewBox="0 0 379.836 379.836"
          overflow="visible"
          xml:space="preserve"
        >
          <rect x="166.148" y="147.295" width="47.541" height="163.934" />
          <rect x="166.148" y="75.41" width="47.541" height="47.541" />
          <circle
            class="circle"
            fill="none"
            stroke-width="20"
            stroke-miterlimit="10"
            cx="189.918"
            cy="189.918"
            r="179.918"
          />
        </svg>
      </slot>
    </div>
  </div>

  <Teleport v-bind:to="teleportTarget">
    <div
      ref="tooltipRef"
      class="bissantzControls tooltipControl tooltip"
      role="tooltip"
      v-show="isVisible"
      v-bind:data-design="contentDesign"
      v-bind:data-locid="$attrs['data-content-locid']"
      v-bind:data-locparam="$attrs['data-label-locparam']"
      v-bind:data-locparam1="$attrs['data-label-locparam1']"
      v-bind:data-locparam2="$attrs['data-label-locparam2']"
      v-bind:data-pak="pak"
      v-bind:class="[size ? `content-${size}` : '', { rounded }]"
      v-bind:style="tooltipStyles"
    >
      <template v-if="content">{{ content }}</template>
      <template v-else-if="$slots.content">
        <slot name="content" />
      </template>
      <div
        v-if="arrow && !mouseMode && (content || $slots.content)"
        ref="arrowRef"
        class="arrow"
        v-bind:style="arrowStyles"
      ></div>
    </div>
  </Teleport>
</template>

<style lang="less">
@import "../styles/components/bc-tooltip.less";
</style>
